import type { DestroyOptions, Point, Sprite } from 'pixi.js';
import { Container, Texture } from 'pixi.js';
import { Signal } from 'typed-signals';
import { getLocalizedValue } from '@softgames/localization';
import type { LevelTaskProgress } from '../types';
import type { TaskViewLayout } from './types';
import { animateBrightnessFlash } from '../../../utils/pixi/animations/filter-animation-utils';

export interface TaskViewConfig {
  layout: TaskViewLayout;
  progress: LevelTaskProgress;
  iconFrame: string;
}

interface TaskCountTextData {
  data?: { template?: string };
}

export class TaskView extends Container {
  readonly #taskLayout: TaskViewLayout;

  protected readonly progress: LevelTaskProgress;

  private readonly initialIconScale: number;

  public readonly onGoalAnimationComplete = new Signal<(taskView: TaskView) => void>();

  constructor({ layout, progress, iconFrame }: TaskViewConfig) {
    super();

    this.#taskLayout = layout;

    const { root } = this.#taskLayout;
    this.addChild(root);

    this.setIcon(iconFrame);

    this.initialIconScale = this.icon.scaleY;

    this.progress = { ...progress };

    this.updateCountText();
  }

  public destroy(options?: DestroyOptions): void {
    this.onGoalAnimationComplete.disconnectAll();
    super.destroy(options);
  }

  public get taskLayout(): TaskViewLayout {
    return this.#taskLayout;
  }

  private setIcon(iconFrame: string): void {
    const { icon } = this.#taskLayout;
    const iconHeight = icon.height;
    icon.texture = Texture.from(iconFrame);
    icon.height = iconHeight;
    icon.scaleX = icon.scaleY;
  }

  public get icon(): Sprite {
    return this.#taskLayout.icon;
  }

  protected updateCountText(): void {
    const { progress, taskLayout } = this;
    const count = getLocalizedValue(Math.max(progress.goal - progress.collected, 0));
    const template = (taskLayout.countText as TaskCountTextData)?.data?.template ?? '#';

    this.taskLayout.countText.text = template.replace('#', count);
  }

  public incrementProgress(amount: number): LevelTaskProgress {
    const { progress } = this;
    progress.collected += amount;
    
    if (this.isTaskVisuallyComplete()) {
      this.showComplete();
    } else {
      this.updateCountText();
    }

    return progress;
  }

  public isTaskVisuallyComplete(): boolean {
    const { progress } = this;
    return progress.collected >= progress.goal;
  }

  public onGoalHit(): void {
    const { icon, initialIconScale } = this;
    const toScale = initialIconScale * 0.9;
    icon.removeTweens();
    icon.scale.set(initialIconScale, initialIconScale);

    icon.tweenTo({ scaleX: toScale, scaleY: toScale }, 300, 'backOut', true, 0, 0, true).onComplete.addOnce(() => {
      this.onGoalAnimationComplete.emit(this);
    });
  }

  public getScoreBubblesTarget(): Point {
    return this.#taskLayout.countText.getGlobalPosition();
  }

  public showComplete(): void {
    const { check, countText } = this.#taskLayout;
    if (check) {
      countText.tweenTo({ alpha: 0 }, 100, 'sinIn', true, 200, 0).onComplete.addOnce(() => {
        animateBrightnessFlash(check, 1.4, 200, 'quadOut', 50);
        check.tweenTo({ scaleX: 1, scaleY: 1 }, 300, 'backOut', true, 0, 0);
      });
    }
  }
}
